import { useContext, useEffect, useState } from 'react'
import { AUTHORIZATION_ERROR_EVENT, axios, clearAuthData } from 'api'
import {
  ApprovedAccount,
  EditPendingAccountParams,
  PendingAccount
} from 'types/accountTypes'
import Cookies from 'js-cookie'
import { SnackbarContext } from 'contexts/SnackbarContext'
import { useNavigate } from 'react-router-dom'
import { cookieOptions, getApiErrorMessage } from 'utils'

export enum AccountStatus {
  APPROVED = 'Approved',
  PENDING = 'Pending'
}
export interface AccountResponseItem {
  accountStatus: AccountStatus
  account: ApprovedAccount | PendingAccount
}

export const useAccounts = () => {
  const [accounts, setAccounts] = useState<AccountResponseItem[]>([])
  const [currentAccount, setCurrentAccount] =
    useState<AccountResponseItem | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalPendingCount, setTotalPendingCount] = useState(1)
  const [status, setStatus] = useState<string>('')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(
    () => !!Cookies.get('adminAccessToken')
  )
  const { showMessage } = useContext(SnackbarContext)
  const navigate = useNavigate()

  useEffect(() => {
    const handleAuthorizationError = () => {
      setIsAuthenticated(false)
      clearAuthData()
    }

    window.addEventListener(AUTHORIZATION_ERROR_EVENT, handleAuthorizationError)
    return () => {
      window.removeEventListener(
        AUTHORIZATION_ERROR_EVENT,
        handleAuthorizationError
      )
    }
  }, [])

  const fetchAccounts = async (
    email?: string,
    newStatus?: string,
    page: number = 1
  ) => {
    setIsLoading(true)
    try {
      const finalStatus = newStatus !== undefined ? newStatus : status

      const response = await axios.get('/accounts', {
        params: {
          ...(email && { email }),
          ...(finalStatus && { status: finalStatus }),
          page
        }
      })

      if (response.status === 200) {
        setAccounts(response.data.accounts)
        setTotalPendingCount(response.data.totalPendingCount)
        setTotalPages(response.data.totalPages || 1)
        setCurrentPage(response.data.currentPage || page)
        setError(null)
      } else {
        setError(response?.data?.error || 'Failed to fetch accounts')
      }
    } catch (error) {
      setError(getApiErrorMessage(error, 'Failed to fetch accounts'))
    } finally {
      setIsLoading(false)
    }
  }

  const fetchAccountById = async (id: string) => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await axios.get(`/accounts/${id}`)
      if (response.status === 200) {
        const accountData = {
          accountStatus: response.data.accountStatus,
          account: response.data.account
        }
        setCurrentAccount(accountData)
        setError(null)
      } else {
        setError(response?.data?.error || 'Account not found')
        setCurrentAccount(null)
      }
    } catch (error) {
      setError(getApiErrorMessage(error, 'Account not found'))
      setCurrentAccount(null)
    } finally {
      setIsLoading(false)
    }
  }

  const updatePendingAccount = async (data: EditPendingAccountParams) => {
    setIsLoading(true)
    try {
      const response = await axios.put('/accounts/pending-account', data)
      if (response.status === 200) {
        setAccounts(prev =>
          prev.map(acc =>
            acc.account.id === data.id
              ? { ...acc, account: { ...acc.account, ...data } }
              : acc
          )
        )
        return { success: true }
      }
      return {
        success: false,
        error: response.data?.error || 'Update failed.'
      }
    } catch (error) {
      return {
        success: false,
        error: getApiErrorMessage(error, 'Update failed.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const logout = () => {
    setIsAuthenticated(false)
    clearAuthData()
    navigate('/login')
  }

  const login = async (email: string, password: string) => {
    setIsLoading(true)
    try {
      const response = await axios.post(
        '/accounts/login',
        { email, password },
        { validateStatus: () => true }
      )
      if (response.status === 200) {
        if (response.data.role === 'ADMIN') {
          const { accessToken, refreshToken } = response.data
          setIsAuthenticated(true)

          Cookies.set('adminAccessToken', accessToken, cookieOptions)
          Cookies.set('adminRefreshToken', refreshToken, cookieOptions)
          axios.defaults.headers.common[
            'Authorization'
          ] = `Bearer ${accessToken}`
          navigate('/')
        } else {
          showMessage('Only administrators are allowed to log in.')
        }
      } else {
        const errorMessage =
          response?.data?.error || 'An unexpected error occurred.'
        showMessage(errorMessage)
      }
    } catch (error) {
      showMessage(
        getApiErrorMessage(
          error,
          'Login failed. Please check your email and password.'
        )
      )
    } finally {
      setIsLoading(false)
    }
  }

  return {
    accounts,
    currentAccount,
    totalPages,
    currentPage,
    status,
    totalPendingCount,
    isLoading,
    error,
    fetchAccounts,
    fetchAccountById,
    setStatus,
    updatePendingAccount,
    logout,
    login,
    isAuthenticated
  }
}
